/**
 * On-screen controls for the Snake (Tram) game.
 *
 * Two layers — both feed the same `onMove(direction)` callback the swipe /
 * keyboard handlers use, so gameplay logic stays in one place:
 *
 *   1. Invisible 4-zone touch overlay covering the entire play arena.
 *      The arena is split into four triangles by its diagonals (top → up,
 *      bottom → down, left → left, right → right) so a quick tap anywhere
 *      on the playfield turns the tram in the natural direction. Sits
 *      *underneath* the visible D-pad in z-order so the D-pad's own zones
 *      win when the player taps over them.
 *
 *   2. Visible D-pad in the bottom-left corner — the same single pad that
 *      shipped with the original design (Frogger keeps a pad on each side;
 *      Snake uses just the one). Reuses the `frogger.controls` sprite as
 *      the visual fallback so the pad looks identical to the other games
 *      until a dedicated Snake controls skin is uploaded.
 */

import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { computeGrid } from './snakeConfig.js';

const PAD_SIZE = 200;
/** Distance from the canvas bottom edge to the D-pad's bottom edge.
 * Was 24 (with an extra 8px lift on top, ~32px total) when the pad
 * lived in the bottom-left corner alongside the perk cards. Now that
 * the pad is centre-anchored between the two card halves, it can sit
 * much lower without colliding with anything — UX direction is to
 * drop it close to the canvas edge so it reads as the dead-bottom
 * thumb-controller. */
const PAD_MARGIN = 4;
/**
 * D-pad opacity. Kept low (≈ 0.55) on purpose so the pad can sit over
 * the bottom of the play arena without obscuring the snake / any
 * obstacles passing under it — see `SNAKE_UI.bottomReserve`, which is
 * deliberately tuned to clear the perk cards but NOT the full D-pad.
 */
const PAD_ALPHA = 0.55;
/** Extra visual lift baked into the bottom-edge calc. Set to 0 now
 * that the pad is centred between the card halves; the perk cards
 * no longer share its vertical lane so it doesn't need to float
 * above them. */
const PAD_BOTTOM_LIFT = 0;

export class SnakeControls {
    container = new Container();
    _game = null;
    _onMove = null;
    /** @type {Container|null} */
    _pad = null;
    /** @type {Graphics|null} — invisible full-arena touch overlay. */
    _arenaZone = null;

    /**
     * @param {*} game
     * @param {(direction: 'up'|'down'|'left'|'right') => void} onMove
     * @param {{ x:number, y:number, width:number, height:number }} [arena]
     *        Optional explicit arena bounds. Falls back to {@link computeGrid}
     *        applied to the game canvas size, which matches the visible
     *        play area in every non-FreeRoam mode.
     */
    constructor(game, onMove, arena = null) {
        this._game = game;
        this._onMove = onMove;
        this._build(arena);
    }

    _build(arena) {
        // Add the invisible arena zone FIRST so the visible D-pad below
        // sits on top of it in pointer-event hit testing — taps inside
        // a D-pad zone keep working as before.
        this._arenaZone = this._buildArenaZones(arena);
        if (this._arenaZone) this.container.addChild(this._arenaZone);

        // Layout: D-pad lives DEAD CENTRE along the bottom edge with the
        // four perk cards split 2-left / 2-right around it. Previously the
        // pad sat in the bottom-left and all four cards ran in a single
        // strip beside it; the new layout matches the UX direction of
        // anchoring the player's primary input under their thumbs in the
        // middle of the screen. `SnakeBottomCards` consumes the same
        // horizontal-reserve number via `centerReserve` so the gap between
        // the two card halves stays exactly wide enough for this pad.
        const baseY = this._game.height - PAD_SIZE - PAD_MARGIN - PAD_BOTTOM_LIFT;
        const pad = this._buildPad();
        pad.x = Math.round((this._game.width - PAD_SIZE) / 2);
        pad.y = baseY;
        this.container.addChild(pad);
        this._pad = pad;
    }

    /**
     * Builds an invisible Graphics rectangle covering the play arena.
     * Splits it into four triangular zones (up/down/left/right) by the
     * diagonals from corner to corner, computed at tap time from the
     * pointer's offset to the arena centre.
     *
     * @param {{ x:number, y:number, width:number, height:number }|null} arena
     */
    _buildArenaZones(arena) {
        const bounds = arena || this._defaultArenaBounds();
        if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;

        const zone = new Graphics();
        zone.rect(bounds.x, bounds.y, bounds.width, bounds.height);
        // alpha=0 still gets hit-tested by pixi as long as eventMode is on.
        zone.fill({ color: 0xffffff, alpha: 0.001 });
        zone.eventMode = 'static';
        zone.cursor = 'pointer';

        const cx = bounds.x + bounds.width  / 2;
        const cy = bounds.y + bounds.height / 2;

        zone.on('pointertap', (e) => {
            if (!this._onMove) return;
            const local = e.getLocalPosition(zone);
            const dx = local.x - cx;
            const dy = local.y - cy;
            // Aspect-corrected triangle split: normalise by half-width /
            // half-height so the diagonals run corner-to-corner regardless
            // of the arena being wider than tall (or vice versa).
            const nx = dx / (bounds.width  / 2);
            const ny = dy / (bounds.height / 2);
            let dir;
            if (Math.abs(nx) > Math.abs(ny)) {
                dir = nx > 0 ? 'right' : 'left';
            } else {
                dir = ny > 0 ? 'down' : 'up';
            }
            this._onMove(dir);
        });

        return zone;
    }

    /** Visible play-area rectangle for the current canvas size. */
    _defaultArenaBounds() {
        if (!this._game?.width || !this._game?.height) return null;
        const g = computeGrid(this._game.width, this._game.height);
        return {
            x: g.originX,
            y: g.originY,
            width:  g.cols * g.cellSize,
            height: g.rows * g.cellSize,
        };
    }

    _buildPad() {
        const pad = new Container();

        // Reuse Frogger's controls skin until Snake gets its own — falls back
        // to a translucent circle if neither is uploaded.
        let sprite = this._game.theme?.createSprite(
            'snake.controls', PAD_SIZE, PAD_SIZE,
            { anchorX: 0, anchorY: 0, fitMode: 'contain' },
        );
        if (!sprite) {
            sprite = this._game.theme?.createSprite(
                'frogger.controls', PAD_SIZE, PAD_SIZE,
                { anchorX: 0, anchorY: 0, fitMode: 'contain' },
            );
        }
        if (sprite) {
            sprite.alpha = PAD_ALPHA;
            pad.addChild(sprite);
        } else {
            const fallback = new Graphics();
            fallback.circle(PAD_SIZE / 2, PAD_SIZE / 2, PAD_SIZE / 2 - 4);
            fallback.fill({ color: 0x0f172a, alpha: PAD_ALPHA * 0.7 });
            fallback.stroke({ color: 0xffffff, alpha: PAD_ALPHA * 0.8, width: 2 });
            pad.addChild(fallback);
        }

        const z = PAD_SIZE / 3;
        const zones = [
            { dir: 'up',    x: z,     y: 0,     w: z, h: z },
            { dir: 'down',  x: z,     y: 2 * z, w: z, h: z },
            { dir: 'left',  x: 0,     y: z,     w: z, h: z },
            { dir: 'right', x: 2 * z, y: z,     w: z, h: z },
        ];
        for (const zo of zones) {
            const hit = new Graphics();
            hit.rect(zo.x, zo.y, zo.w, zo.h);
            hit.fill({ color: 0xffffff, alpha: 0.001 });
            hit.eventMode = 'static';
            hit.cursor = 'pointer';
            hit.on('pointertap', () => {
                if (this._onMove) this._onMove(zo.dir);
            });
            pad.addChild(hit);
        }
        return pad;
    }

    /** Toggle visibility (used on game-over / win). */
    setVisible(v) {
        this.container.visible = !!v;
    }

    destroy() {
        if (this._arenaZone) {
            this._arenaZone.removeAllListeners?.();
            if (this._arenaZone.parent) this._arenaZone.parent.removeChild(this._arenaZone);
            this._arenaZone = null;
        }
        if (this._pad) {
            this._pad.removeChildren();
            if (this._pad.parent) this._pad.parent.removeChild(this._pad);
            this._pad = null;
        }
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this.container.removeChildren();
        this._onMove = null;
        this._game = null;
    }
}
